import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Users, ArrowLeft } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventsPage = () => {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // SEO per lingua
  const seoTitles = {
    it: 'Eventi e Tour Offroad | Offroad Italia',
    en: 'Offroad Events & Tours | Offroad Italia',
    de: 'Offroad-Events & Touren | Offroad Italia',
    fr: 'Événements & Tours Offroad | Offroad Italia',
    es: 'Eventos y Tours Offroad | Offroad Italia'
  };
  
  const seoDescriptions = {
    it: 'Scopri tutti i nostri tour offroad in moto: Tunisia, Libia, Italia, Grecia, Islanda e Colombia. Prenota la tua prossima avventura!',
    en: 'Discover all our offroad motorcycle tours: Tunisia, Libya, Italy, Greece, Iceland and Colombia. Book your next adventure!',
    de: 'Entdecken Sie alle unsere Offroad-Motorradtouren: Tunesien, Libyen, Italien, Griechenland, Island und Kolumbien.',
    fr: 'Découvrez tous nos tours moto offroad: Tunisie, Libye, Italie, Grèce, Islande et Colombie.',
    es: 'Descubre todos nuestros tours offroad en moto: Túnez, Libia, Italia, Grecia, Islandia y Colombia.'
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, countriesRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/countries?active_only=true`)
      ]);
      setEvents(eventsRes.data);
      // Map country data to expected format
      const mappedCountries = countriesRes.data.map(c => ({
        id: c.code,
        name: c.name,
        flag: c.flag_url,
        keywords: c.keywords || []
      }));
      setCountries(mappedCountries);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterByCountry = (country) => {
    setSelectedCountry(country);
  };

  // Get filtered events based on selected country
  const matchesCountry = (event, country) => {
    // Priority 1: explicit country field set by admin
    if (event.country) return event.country === country.id;
    // Fallback (legacy): match by keywords in title/location for events without `country`
    const haystack = `${event.title_it || ''} ${event.location || ''}`.toLowerCase();
    return country.keywords.some(keyword => haystack.includes(keyword.toLowerCase()));
  };

  const getFilteredEvents = () => {
    if (!selectedCountry) return [];
    return events.filter(event => matchesCountry(event, selectedCountry));
  };

  const clearFilter = () => {
    setSelectedCountry(null);
  };

  const getLocalizedTitle = (event) => {
    const lang = i18n.language;
    return event[`title_${lang}`] || event.title_it;
  };

  const getLocalizedDescription = (event) => {
    const lang = i18n.language;
    const desc = event[`description_${lang}`] || event.description_it;
    return desc.length > 150 ? desc.substring(0, 150) + '...' : desc;
  };

  // Count events per country
  const getCountryEventCount = (country) => {
    return events.filter(event => matchesCountry(event, country)).length;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={seoTitles[i18n.language] || seoTitles.it}
        description={seoDescriptions[i18n.language] || seoDescriptions.it}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-white mb-4">{t('events.title')}</h1>
          <p className="text-xl text-blue-100">{i18n.language === 'it' ? 'Scegli la tua destinazione' : i18n.language === 'en' ? 'Choose your destination' : i18n.language === 'de' ? 'Wähle dein Ziel' : i18n.language === 'fr' ? 'Choisissez votre destination' : 'Elige tu destino'}</p>
        </div>
      </div>

      {/* Country Selection or Events List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {!selectedCountry ? (
          /* Country Selection Grid */
          <div>
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
              {i18n.language === 'it' ? 'Dove vuoi andare?' : i18n.language === 'en' ? 'Where do you want to go?' : i18n.language === 'de' ? 'Wohin möchten Sie?' : i18n.language === 'fr' ? 'Où voulez-vous aller?' : '¿A dónde quieres ir?'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {countries.map((country) => {
                const eventCount = getCountryEventCount(country);
                const isComingSoon = country.id === 'colombia';
                return (
                  <button
                    key={country.id}
                    onClick={() => filterByCountry(country)}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-6 flex flex-col items-center group hover:scale-105"
                    data-testid={`country-btn-${country.id}`}
                  >
                    <img 
                      src={country.flag} 
                      alt={country.name}
                      className="w-20 h-14 object-cover rounded-lg shadow-md mb-4 group-hover:scale-110 transition-transform"
                    />
                    <span className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                      {country.name}
                    </span>
                    {isComingSoon ? (
                      <span className="text-sm text-orange-500 font-bold mt-1 animate-pulse">
                        COMING SOON
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500 mt-1">
                        {eventCount} {eventCount === 1 ? (i18n.language === 'it' ? 'evento' : i18n.language === 'en' ? 'event' : i18n.language === 'de' ? 'Event' : i18n.language === 'fr' ? 'événement' : 'evento') : (i18n.language === 'it' ? 'eventi' : i18n.language === 'en' ? 'events' : i18n.language === 'de' ? 'Events' : i18n.language === 'fr' ? 'événements' : 'eventos')}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Info text */}
            <div className="text-center mt-12 text-gray-600">
              <p className="text-lg">
                {i18n.language === 'it' ? 'Clicca su una nazione per vedere tutti gli eventi disponibili' : i18n.language === 'en' ? 'Click on a country to see all available events' : i18n.language === 'de' ? 'Klicken Sie auf ein Land, um alle verfügbaren Events zu sehen' : i18n.language === 'fr' ? 'Cliquez sur un pays pour voir tous les événements disponibles' : 'Haz clic en un país para ver todos los eventos disponibles'}
              </p>
            </div>
          </div>
        ) : (
          /* Events List for Selected Country */
          <div>
            {/* Back Button */}
            <button
              onClick={clearFilter}
              className="flex items-center text-blue-600 hover:text-blue-800 font-semibold mb-8 transition-colors"
              data-testid="back-to-countries-btn"
            >
              <ArrowLeft className="mr-2" size={24} />
              {t('common.back')}
            </button>

            {/* Country Header */}
            <div className="flex items-center justify-center mb-8">
              <img 
                src={selectedCountry.flag} 
                alt={selectedCountry.name}
                className="w-16 h-12 object-cover rounded-lg shadow-md mr-4"
              />
              <h2 className="text-4xl font-bold text-gray-900">
                {i18n.language === 'it' ? 'Eventi in' : i18n.language === 'en' ? 'Events in' : i18n.language === 'de' ? 'Events in' : i18n.language === 'fr' ? 'Événements en' : 'Eventos en'} {selectedCountry.name}
              </h2>
            </div>

            {/* Events Grid */}
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : getFilteredEvents().length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-md">
                <p className="text-xl text-gray-600">{t('events.noEvents')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {getFilteredEvents().map((event) => (
                  <div 
                    key={event.id} 
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow"
                    data-testid={`event-card-${event.id}`}
                  >
                    <div 
                      className="h-56 bg-cover bg-top relative" 
                      style={{ backgroundImage: `url(${event.image_url})` }}
                    >
                      {event.status === 'sold_out' && (
                        <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-lg font-semibold">
                          {t('events.soldOut')}
                        </div>
                      )}
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{getLocalizedTitle(event)}</h3>
                      <p className="text-gray-600 mb-4 text-sm">{getLocalizedDescription(event)}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg border-2 border-blue-200">
                          <div className="flex items-center text-blue-900">
                            <Calendar size={20} className="mr-2 flex-shrink-0" />
                            <span className="font-bold text-sm">{event.date_start} → {event.date_end}</span>
                          </div>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <MapPin size={18} className="mr-2 flex-shrink-0" />
                          <span className="text-sm">{event.location}</span>
                        </div>
                        {event.spots_available !== null && event.spots_total !== null && (
                          <div className="bg-green-50 p-3 rounded-lg border-2 border-green-300">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-green-900">
                                <Users size={20} className="mr-2 flex-shrink-0" />
                                <span className="font-bold text-sm">
                                  {event.spots_available}/{event.spots_total} {t('events.spots').toLowerCase().split(' ')[0]}
                                </span>
                              </div>
                              {event.spots_available <= 5 && event.spots_available > 0 && (
                                <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold animate-pulse">
                                  {i18n.language === 'it' ? 'ULTIMI!' : i18n.language === 'en' ? 'LAST!' : i18n.language === 'de' ? 'LETZTE!' : i18n.language === 'fr' ? 'DERNIERS!' : '¡ÚLTIMOS!'}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="border-t pt-4">
                        <div className="mb-3">
                          {event.price_rent && (
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600">{t('events.rentalBike')}:</span>
                              <span className="font-semibold text-blue-600">€{event.price_rent}</span>
                            </div>
                          )}
                          {event.price_own && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">{t('events.ownBike')}:</span>
                              <span className="font-semibold text-blue-600">€{event.price_own}</span>
                            </div>
                          )}
                        </div>
                        
                        <Link
                          to={`/events/${event.id}`}
                          className="block text-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                          data-testid={`view-details-btn-${event.id}`}
                        >
                          {t('events.details')}
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
